import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2 } from 'lucide-react';
import { userService } from '../../../_services';

function SubDistributorDialog({ open, onOpenChange, distributor }) {
  const [loading, setLoading] = useState(false);
  const [subDistData, setSubDistData] = useState(null); // { direct, override, secOverride }
  const [activeTab, setActiveTab] = useState('direct');

  useEffect(() => {
    if (open && distributor) {
      fetchSubDistData();
      setActiveTab('direct');
    }
  }, [open, distributor]);

  const fetchSubDistData = async () => {
    setLoading(true);
    try {
      // NewReport (dev-v3 default) — uses SecondaryCommTrackingSubDist_Tier_NonTier
      const response = await userService.SecondaryCommTrackingSubDist_Tier_NonTier(
        distributor.dateMonth,
        distributor.distributor_UserId
      );
      // API returns array: [{ direct: [...], tiered: [...], override: [...], secOverride: [...] }]
      if (response && response.length > 0) {
        setSubDistData(response[0]);
      } else {
        setSubDistData(null);
      }
    } catch (error) {
      console.error('Error fetching sub-distributor data:', error);
      setSubDistData(null);
    } finally {
      setLoading(false);
    }
  };

  const renderTable = (rows, columns) => (
    <Table>
      <TableHeader>
        <TableRow>
          {columns.map(col => (
            <TableHead key={col}>{col}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows && rows.length > 0 ? (
          rows.map((item, index) => (
            <TableRow key={index}>
              <TableCell>{item.groupNumber}</TableCell>
              <TableCell>{item.distributorName}</TableCell>
              <TableCell>{item.paidClaims}</TableCell>
              <TableCell>${item.commission?.toFixed(2) ?? '0.00'}</TableCell>
              <TableCell>${item.earnedComm?.toFixed(2) ?? '0.00'}</TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={columns.length} className="text-center text-muted-foreground py-6">
              No Record Found
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {distributor?.distributorName}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-accent" />
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-1 min-h-0">
            <div className="flex">
            <TabsList>
              <TabsTrigger value="direct">Direct</TabsTrigger>
              <TabsTrigger value="override">Override</TabsTrigger>
              <TabsTrigger value="secOverride">Secondary Override</TabsTrigger>
            </TabsList>
            </div>

            {/* Direct */}
            <TabsContent value="direct" className="flex-1 min-h-0 overflow-y-auto">
              {renderTable(
                subDistData?.direct,
                ['Group Number', 'Distributor', 'Transaction Count', 'Commission($)', 'Total($)']
              )}
            </TabsContent>

            {/* Override */}
            <TabsContent value="override" className="flex-1 min-h-0 overflow-y-auto">
              {renderTable(
                subDistData?.override,
                ['Group Number', 'Override User (if any)', 'Transaction Count', 'Commission($)', 'Total($)']
              )}
            </TabsContent>

            {/* Secondary Override */}
            <TabsContent value="secOverride" className="flex-1 min-h-0 overflow-y-auto">
              {renderTable(
                subDistData?.secOverride,
                ['Group Number', 'Override User (if any)', 'Transaction Count', 'Commission($)', 'Total($)']
              )}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default SubDistributorDialog;
